import { useRef, useEffect, useCallback } from 'react';

export default function AddressAutocomplete({ value, onChange, onPlaceSelect, className, placeholder, ...props }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const onChangeRef = useRef(onChange);
  onPlaceSelectRef.current = onPlaceSelect;
  onChangeRef.current = onChange;

  useEffect(() => {
    const el = inputRef.current;
    if (!el || autocompleteRef.current) return;

    // Wait for Google Maps to be ready
    const initAutocomplete = () => {
      if (!window.google?.maps?.places?.Autocomplete) return false;
      const ac = new window.google.maps.places.Autocomplete(el, {
        types: ['address'],
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address', 'geometry'],
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (!place?.address_components) return;
        const comp = {};
        place.address_components.forEach(c => c.types.forEach(t => { comp[t] = c; }));
        const streetNumber = comp.street_number?.long_name || '';
        const route = comp.route?.long_name || '';
        const street = [streetNumber, route].filter(Boolean).join(' ');
        const city = comp.locality?.long_name || comp.sublocality?.long_name || '';
        const state = comp.administrative_area_level_1?.short_name || '';
        const zip = comp.postal_code?.long_name || '';
        const county = (comp.administrative_area_level_2?.long_name || '').replace(' County', '');
        if (onChangeRef.current) onChangeRef.current(street);
        if (onPlaceSelectRef.current) onPlaceSelectRef.current({ street, city, state, zip, county, formatted: place.formatted_address || '', lat: place.geometry?.location?.lat?.(), lng: place.geometry?.location?.lng?.() });
      });
      autocompleteRef.current = ac;
      return true;
    };
    if (!initAutocomplete()) {
      // Retry every 500ms until Google is loaded
      const interval = setInterval(() => { if (initAutocomplete()) clearInterval(interval); }, 500);
      return () => clearInterval(interval);
    }
  }, []);

  const handleInput = useCallback((e) => {
    if (onChangeRef.current) onChangeRef.current(e.target.value);
  }, []);

  // Use uncontrolled input — Google Places needs full DOM control
  // Set initial value via defaultValue, sync resets via key
  return (
    <input
      ref={inputRef}
      defaultValue={value}
      onInput={handleInput}
      className={`flex h-9 w-full rounded-md border px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50 ${className || ''}`}
      placeholder={placeholder || 'Start typing address...'}
      type="text"
      autoComplete="off"
      {...props}
    />
  );
}
